import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { UnitClass } from '../../data/types';
import { parsePriceRows } from '../../domain/priceImport';
import { date, unitPrice } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { StatusChip } from '../../ui/primitives';

const sample = (mid: number) =>
  [
    'date,entry,mid,exit',
    `2026-10-31,${(mid * 1.004).toFixed(4)},${(mid * 1.002).toFixed(4)},${mid.toFixed(4)}`,
    `2026-11-30,${(mid * 1.006).toFixed(4)},${(mid * 1.004).toFixed(4)},${(mid * 1.002).toFixed(4)}`,
  ].join('\n');

export default function PriceImportDialog({
  unitClass,
  open,
  onClose,
}: {
  unitClass: UnitClass;
  open: boolean;
  onClose: () => void;
}) {
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [text, setText] = useState(() => sample(unitClass.unitPrice));
  const rows = useMemo(
    () => parsePriceRows(text, unitClass.unitPrice),
    [text, unitClass.unitPrice],
  );
  const problems = rows.filter((row) => row.problem).length;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Import prices: {unitClass.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Paste the rows from the administrator&apos;s NAV report, or a CSV with date, entry, mid
          and exit. Each row is checked before anything is published.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          value={text}
          onChange={(event) => setText(event.target.value)}
          inputProps={{
            'aria-label': 'Price rows',
            style: { fontFamily: 'monospace', fontSize: 13 },
          }}
        />
        <DataTable
          dense
          rows={rows}
          rowKey={(row) => `${row.date}-${row.mid}`}
          emptyMessage="No rows yet"
          columns={[
            {
              key: 'date',
              label: 'Effective',
              render: (row) => (row.problem && !row.date ? '—' : date(row.date)),
            },
            {
              key: 'entry',
              label: 'Entry',
              align: 'right',
              render: (row) => unitPrice(row.entry || 0),
            },
            {
              key: 'mid',
              label: 'Mid (NAV)',
              align: 'right',
              render: (row) => unitPrice(row.mid || 0),
            },
            {
              key: 'exit',
              label: 'Exit',
              align: 'right',
              render: (row) => unitPrice(row.exit || 0),
            },
            {
              key: 'check',
              label: 'Check',
              render: (row) =>
                row.problem ? (
                  <StatusChip status="FAILED" label={row.problem} />
                ) : row.warning ? (
                  <StatusChip status="PENDING" label={row.warning} />
                ) : (
                  <StatusChip status="DONE" label="OK" />
                ),
            },
          ]}
        />
        {problems > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            Fix {problems === 1 ? 'the row' : `${problems} rows`} marked above before publishing.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!rows.length || problems > 0}
          onClick={() => {
            dispatch({
              type: 'UNIT_PRICES_IMPORT',
              classId: unitClass.id,
              rows: rows.map(({ date: day, entry, mid, exit }) => ({
                date: day,
                entry,
                mid,
                exit,
              })),
            });
            toast(`${rows.length} prices published`);
            onClose();
          }}
        >
          Publish {rows.length} prices
        </Button>
      </DialogActions>
    </Dialog>
  );
}
